<template>
    <div class="project-create-back relative z-10 flex h-[60px] items-center border-b border-gray-40 px-6">
        <div @click="router.push({ name: 'home' })" class="mr-6 flex h-6 shrink-0 cursor-pointer items-center text-sm leading-6 text-black">
            <PtIcon icon="SolidArrowLeft" class="mr-1 fill-grey-450 text-2xl" />
            {{ t('report.back') }}
        </div>
    </div>

    <div class="project-create-chat flex h-[calc(100vh-124px)]  justify-between" v-show="copilotDone">
        <ProjectIcon ref="projectIconRef" v-if="currentProject" class="hidden shrink-0" :icon="currentProject.icon" size="md" />
        <ChatWithCopilot class=" w-full grow bg-grey-10 py-5" :url="url" />
        <Step :key="`${route.params?.projectId}-${route.query?.ts}`" ref="stepRef" class="w-[470px] shrink-0" />
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { PtIcon } from 'patsnap-biz';
import dayjs from 'dayjs';
import { rebind } from '@/inversify.config';
import { getFiveMinutesRandomNumber } from '@eureka/utils/random-time';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';

import ChatWithCopilot from './ChatWithCopilot.vue';
import Step from './Step.vue';
import { ProjectCopilotServiceImpl } from '@/services/project-copilot-service';
import { PatsnapCopilotService } from '@analytics/patsnap-copilot/src/patsnap-copilot-service.interface';
import { PatsnapCopilotUsageService } from '@analytics/patsnap-copilot/src/patsnap-copilot-usage-service';
import { WuJieBus } from '@analytics/patsnap-copilot/src/wujie-bus';
import { CopilotChatUsageServiceImpl } from '@eureka/services/src/copilot-chat-usage-service';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { LocalStorageService } from '@eureka/services/src/storage-service'
import { useI18n } from 'vue-i18n';
import _ from 'lodash'

import '@/assets/icons/copilot-white.svg';
import { EurekaProduct, controlDeferred } from '@eureka/utils/control';
import { getProjectInfo } from '@/infrastructure/api-functions/report';
import ProjectIcon from '@/components/ProjectIcon.vue'
import type { IProject } from '@/types';

rebind(PatsnapCopilotUsageService).to(CopilotChatUsageServiceImpl);
rebind(PatsnapCopilotService).to(ProjectCopilotServiceImpl);

const url = `${import.meta.env.VITE_MF_EUREKA_RESULT_COPILOT}/chat.html?t=${getFiveMinutesRandomNumber()}`;

const wujieBus = injectService<WuJieBus>(WuJieBus);
const copilotService = injectService<PatsnapCopilotService>(PatsnapCopilotService) as unknown as ProjectCopilotServiceImpl;

const { t } = useI18n();

const copilotDone = ref(false)

const stepRef = ref()
const route = useRoute();
const router = useRouter();
const isMaterialUser = ref(false)
const currentProject = ref<Nullable<IProject>>(null)
const projectIconRef = ref(null);

const getCurrentProject = function() {
    return getProjectInfo(route.params.projectId);
}

function setInputDisabled(v: boolean) {
    wujieBus.$emit('copilot-input-disabled', v);
}

/**
 * 从seek selection页面跳转过来，发送Seek划段选中文本消息
 */
function handleSendTextFromSeekSelection() {
    const question_id = route.query.question_id as string;
    if (!question_id) return;
    const cacheQuestionText = LocalStorageService.get(question_id);
    if (cacheQuestionText) {
        wujieBus.$emit('send-message', {
            text: cacheQuestionText,
        });
        LocalStorageService.delete(question_id);
    }
}

async function handleCopilotLoaded() {
    copilotDone.value = true;

    if (!copilotService.jobId) {
        setInputDisabled(false);
        copilotService.newSession();
        handleSendTextFromSeekSelection();
    } else {
        setInputDisabled(true);
        const current = await getCurrentProject();
        currentProject.value = current;
        await nextTick();
        const imgSrc = projectIconRef.value?.$el?.getAttribute('src');
        copilotService.newSession({
            text: null,
            type: 'BoardFinish',
            data: {
                icon: `<img style="width: 64px" src="${imgSrc}" />`,
                title: current.project_title,
                date: current.created_at ? dayjs(current.created_at).format('YYYY-MM-DD') : '',
                onClick: async() => {
                    await router.push({
                        name: 'report',
                        params: {
                            projectId: current.project_id,
                        },
                    })
                    wujieBus.$emit('clean-message', true);
                },
            },
        });
    }
}

watch(route, async({ params }) => {
    const { product } = await controlDeferred.promise
    isMaterialUser.value = product === EurekaProduct.MATERIAL
    copilotService.setJobId(params.projectId as string);
    stepRef?.value?.updateStatus(copilotService.jobId);
    handleCopilotLoaded();
}, { immediate: true, deep: true })

copilotService.setDefaultMessage({
    text: null,
    type: 'ProjectWelcome',
    data: {
        title: t('chat.welcome'),
        text: t('chat.example'),
    },
})

copilotService.onGenerate(() => {
    setInputDisabled(true);
    return stepRef.value.analysis(copilotService.jobId).then(() => {
        wujieBus.$emit('send-message', {
            text: null,
            type: 'plain_text',
            data: {
                content: t('chat.confirm'),
            },
        });
    }).catch(e => {
        setInputDisabled(false);
    });
})

function copilotBusyChange(isBusy: boolean, turns: any[]) {
    if (!isBusy) {
        const lastTurn = _.last(turns);
        copilotService.lastInput = lastTurn?.input?.text || '';
        if (_.get(lastTurn, 'reply.detail.message.current_question')) {
            stepRef.value.updateStatus(copilotService.jobId, lastTurn.reply.detail.message.current_question);
        }
    }
}
wujieBus.$on('copilot-busy-change', copilotBusyChange);
wujieBus.$on('copilot-loaded', handleCopilotLoaded)

onBeforeUnmount(() => {
    wujieBus.$off('copilot-busy-change', copilotBusyChange);
    wujieBus.$off('copilot-loaded', handleCopilotLoaded);
})
</script>
